/**
 * useGamificationNotifier
 *
 * Polling-basierter Hook der die Gamification-Summary ueberwacht
 * und bei Aenderungen (Level-Up, neues Badge) Toast-Benachrichtigungen zeigt.
 *
 * Wird einmal im AppLayout eingebunden und laeuft im Hintergrund.
 */
import { useEffect, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { AYU_LEVELS, BADGES } from "../../../shared/gamificationConfig";

interface GamificationSnapshot {
  totalPoints: number;
  levelId: number;
  badgeCount: number;
  streakCurrent: number;
}

export function useGamificationNotifier(clientId: string | undefined) {
  const prevSnapshot = useRef<GamificationSnapshot | null>(null);
  const isFirstLoad = useRef(true);

  const summary = trpc.gamification.summary.useQuery(
    { clientId: clientId ?? "" },
    {
      enabled: !!clientId,
      refetchInterval: 30_000, // Alle 30 Sekunden pruefen
      refetchOnWindowFocus: true,
      staleTime: 15_000,
    }
  );

  useEffect(() => {
    if (!summary.data) return;

    const current: GamificationSnapshot = {
      totalPoints: summary.data.totalPoints,
      levelId: summary.data.level?.id ?? 1,
      badgeCount: summary.data.badges?.length ?? 0,
      streakCurrent: summary.data.streaks?.[0]?.currentStreak ?? 0,
    };

    // Beim ersten Laden nur Snapshot speichern, keine Toasts
    if (isFirstLoad.current || !prevSnapshot.current) {
      prevSnapshot.current = current;
      isFirstLoad.current = false;
      return;
    }

    const prev = prevSnapshot.current;

    // Level-Up erkennen
    if (current.levelId > prev.levelId) {
      const level = AYU_LEVELS.find((l) => l.id === current.levelId);
      if (level) {
        toast.success(`Level-Up! ${level.name} (${level.nameSanskrit})`, {
          description: `Du hast Level ${level.id} erreicht — ${level.description}`,
          duration: 7000,
        });
      }
    }

    // Neues Badge erkennen
    if (current.badgeCount > prev.badgeCount) {
      const diff = current.badgeCount - prev.badgeCount;
      // Wir kennen nicht genau welches Badge neu ist, aber zeigen eine generische Nachricht
      toast.success(
        diff === 1 ? "Neues Abzeichen verdient!" : `${diff} neue Abzeichen verdient!`,
        {
          description: "Schau in deinem Gamification-Bereich nach!",
          duration: 5000,
        }
      );
    }

    // Punkte-Zuwachs (nur bei groesseren Spruengen, z.B. >= 50 Punkte)
    const pointsDiff = current.totalPoints - prev.totalPoints;
    if (pointsDiff >= 50) {
      toast(`+${pointsDiff} Prana-Punkte gesammelt!`, {
        duration: 3000,
      });
    }

    // Streak-Meilenstein (7, 14, 30, 60, 100 Tage)
    const streakMilestones = [7, 14, 30, 60, 100];
    for (const milestone of streakMilestones) {
      if (current.streakCurrent >= milestone && prev.streakCurrent < milestone) {
        toast.success(`${milestone}-Tage-Streak erreicht!`, {
          description: "Deine Bestaendigkeit zahlt sich aus.",
          duration: 5000,
        });
        break;
      }
    }

    prevSnapshot.current = current;
  }, [summary.data]);
}
